import { AxiosError } from 'axios'

/**
 * Code posé par l'API sur toutes les expirations de session de recherche
 * (apps/server/src/api/shared/errors/search-session-errors.ts).
 */
const EXPIRED_SEARCH_SESSION_CODE = 'SEARCH_SESSION_EXPIRED'

/**
 * Repli pour les versions d'API antérieures à `errorCode`. Deux formulations
 * coexistent : « Session de recherche expirée » (400 à la création de session) et
 * « Session expirée » (410 sur une BookingSession morte). Le second motif couvre
 * le premier, mais on garde les deux pour que l'intention reste lisible.
 */
const EXPIRED_SEARCH_SESSION_TEXTS = ['session de recherche expirée', 'session expirée']

/**
 * Messages génériques que NestJS renvoie sur un 500 non maîtrisé. Ils ne doivent
 * jamais s'afficher : le serveur les remplace désormais (ClientFacingExceptionFilter),
 * on garde ce garde-fou côté client pour les versions d'API plus anciennes.
 */
const GENERIC_SERVER_MESSAGES = new Set([
  'bad gateway',
  'gateway timeout',
  'internal server error',
  'service unavailable',
])

interface ApiErrorResponse {
  errorCode?: string
  message?: string
}

const getResponseData = (error: unknown): ApiErrorResponse | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined
  }
  return error.response?.data as ApiErrorResponse | undefined
}

export const getApiErrorMessage = (error: unknown): string | undefined => {
  const message = getResponseData(error)?.message
  return typeof message === 'string' ? message : undefined
}

/** Code métier posé par l'API (`errorCode` dans le corps de la réponse). */
const getApiErrorCode = (error: unknown): string | undefined => {
  const errorCode = getResponseData(error)?.errorCode
  return typeof errorCode === 'string' ? errorCode : undefined
}

/**
 * Message affichable tel quel au client. On refuse le message de l'API quand il
 * s'agit d'un texte technique générique (« Internal server error ») ou d'un 5xx
 * sans `errorCode` — dans ces cas-là, rien n'a été rédigé pour l'utilisateur.
 */
export const getClientFacingMessage = (error: unknown, fallback: string): string => {
  const message = getApiErrorMessage(error)

  if (!message) {
    return fallback
  }

  if (GENERIC_SERVER_MESSAGES.has(message.trim().toLowerCase())) {
    return fallback
  }

  const status = error instanceof AxiosError ? error.response?.status : undefined
  if (status !== undefined && status >= 500 && !getApiErrorCode(error)) {
    return fallback
  }

  return message
}

const hasStatusAndCode = (error: unknown, status: number, errorCode: string): boolean =>
  error instanceof AxiosError &&
  error.response?.status === status &&
  getApiErrorCode(error) === errorCode

export const isExpiredSearchSessionError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false
  }

  if (getApiErrorCode(error) === EXPIRED_SEARCH_SESSION_CODE) {
    return true
  }

  const status = error.response?.status
  if (status !== 400 && status !== 410) {
    return false
  }

  const message = getApiErrorMessage(error)?.toLowerCase()
  return !!message && EXPIRED_SEARCH_SESSION_TEXTS.some((text) => message.includes(text))
}

/**
 * True when the flight search failed because Affidium (our flight provider) is
 * down — the server answers HTTP 503 with errorCode 'PROVIDER_UNAVAILABLE'.
 * Lets the UI show a "maintenance" message instead of a generic error.
 */
export const isProviderUnavailableError = (error: unknown): boolean =>
  hasStatusAndCode(error, 503, 'PROVIDER_UNAVAILABLE')

/**
 * Vrai quand la création du dossier chez Affidium a échoué alors qu'on sait
 * qu'aucune réservation n'a été enregistrée : le client peut relancer.
 */
export const isBookingProviderUnavailableError = (error: unknown): boolean =>
  hasStatusAndCode(error, 503, 'BOOKING_PROVIDER_UNAVAILABLE')

/**
 * Vrai quand Affidium n'a pas confirmé l'issue de la réservation (silence, réponse
 * illisible) : un dossier existe peut-être. Il ne faut surtout pas relancer, mais
 * passer par l'agence.
 */
export const isBookingProviderUncertainError = (error: unknown): boolean =>
  hasStatusAndCode(error, 503, 'BOOKING_PROVIDER_UNCERTAIN')

/** Vrai pour l'un ou l'autre des deux échecs de réservation ci-dessus. */
export const isBookingProviderError = (error: unknown): boolean =>
  isBookingProviderUnavailableError(error) || isBookingProviderUncertainError(error)

/**
 * Discriminates the two 429 throttles emitted by the API:
 * - 'minute' → per-minute window (FlightSearchThrottle, ChatThrottle…)
 * - 'day'    → daily quota (DailySearchThrottlerGuard…)
 * Reads the structured `code` field set by the server guards; falls back to a
 * message pattern-match so older server versions still resolve correctly.
 */
export const getRateLimitScope = (error: unknown): 'day' | 'minute' | null => {
  if (!(error instanceof AxiosError) || error.response?.status !== 429) {
    return null
  }
  const data = error.response?.data as undefined | { code?: unknown; message?: unknown }
  if (data?.code === 'day' || data?.code === 'minute') return data.code
  const message = typeof data?.message === 'string' ? data.message.toLowerCase() : ''
  if (
    message.includes("aujourd'hui") ||
    message.includes('demain') ||
    message.includes('quotidien')
  ) {
    return 'day'
  }
  if (message) return 'minute'
  return 'minute'
}

/**
 * True quand le prestataire de paiement (SSP, FLOA) est en panne ou en
 * maintenance : le serveur répond 503 avec errorCode
 * 'PAYMENT_PROVIDER_UNAVAILABLE'. Rien à corriger côté client, seulement à
 * réessayer plus tard.
 */
export const isPaymentProviderUnavailableError = (error: unknown): boolean =>
  hasStatusAndCode(error, 503, 'PAYMENT_PROVIDER_UNAVAILABLE')

/**
 * True quand le prestataire a refusé la demande pour une raison métier (ex.
 * numéro de téléphone invalide) : 400 + errorCode 'PAYMENT_PROVIDER_REJECTED'.
 * Contrairement à une panne, l'utilisateur doit corriger sa saisie.
 */
export const isPaymentProviderRejectedError = (error: unknown): boolean =>
  hasStatusAndCode(error, 400, 'PAYMENT_PROVIDER_REJECTED')

export const isPaymentAlreadyInitiatedError = (error: unknown): boolean =>
  hasStatusAndCode(error, 409, 'PAYMENT_ALREADY_INITIATED')
